import type { LatLng, Station, StationKind } from '@/state/types';
import { haversineKm } from './geo';
import { MIN_STATION_DISTANCE_M } from '@/data/stations';
import { getRadiusKm } from '@/data/leveling';

export type ValidationResult =
  | { ok: true }
  | { ok: false; reason: 'no-home-city' | 'out-of-radius' | 'too-close'; message: string };

export interface ValidationContext {
  candidate: LatLng;
  kind: StationKind;
  stations: Station[];
  homeCityCenter: LatLng | null;
  level: number;
}

/**
 * Validates whether a station can be built at `candidate`.
 * Rules (per spec):
 *   1. Must be inside global radius = f(level) around homeCity.
 *   2. Must not be closer than MIN_STATION_DISTANCE_M to any existing
 *      station of the same kind. Different kinds may overlap.
 */
export function validateStationPlacement(ctx: ValidationContext): ValidationResult {
  if (!ctx.homeCityCenter) {
    return {
      ok: false,
      reason: 'no-home-city',
      message: 'Kein Startort gewählt.',
    };
  }

  const radiusKm = getRadiusKm(ctx.level);
  const distFromCityKm = haversineKm(ctx.homeCityCenter, ctx.candidate);
  if (distFromCityKm > radiusKm) {
    return {
      ok: false,
      reason: 'out-of-radius',
      message: `Außerhalb des Einsatzgebiets (${distFromCityKm.toFixed(1)} km / erlaubt ${radiusKm} km). Level erhöhen für mehr Reichweite.`,
    };
  }

  // Hospitals (NPC) are exempt from the same-kind proximity rule.
  if (ctx.kind !== 'hospital') {
    for (const existing of ctx.stations) {
      if (existing.kind !== ctx.kind) continue;
      const distM = haversineKm(existing.position, ctx.candidate) * 1000;
      if (distM < MIN_STATION_DISTANCE_M) {
        return {
          ok: false,
          reason: 'too-close',
          message: `Mindestabstand ${MIN_STATION_DISTANCE_M} m zur Wache „${existing.name}" unterschritten (${Math.round(distM)} m).`,
        };
      }
    }
  }

  return { ok: true };
}
